// US-078 — Mode-specific footer.
//
// Renders the appropriate banner for the current state:
//   - pending (magenta pause badge),
//   - auto (green badge, ghost "Ajuster la voix IA"),
//   - review (purple badge, ghost "Ajuster la voix IA"),
//   - hot-override (red badge, validation-required copy).
//
// Mirrors `ls-inbox.jsx:885-981`.

import { useT } from '@barel/i18n';
import { Button } from '@barel/ui';

export type FooterMode = 'pending' | 'auto' | 'review' | 'hot-override';

export interface FooterProps {
  mode: FooterMode;
  pendingActionLabel?: string;
  /** When provided, the ghost "Ajuster la voix IA" CTA renders. */
  onNavSettings?: () => void;
}

export function Footer({ mode, pendingActionLabel, onNavSettings }: FooterProps) {
  const t = useT('inbox');
  const palette = paletteForMode(mode);

  return (
    <div
      data-ls-component="thread-footer"
      data-mode={mode}
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderTop: '1px solid var(--border-subtle)',
        background: 'var(--bg-surface)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: '10px 14px',
          borderRadius: 10,
          background: palette.bg,
          border: `1px solid ${palette.border}`,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 28,
            height: 28,
            flexShrink: 0,
            borderRadius: '50%',
            background: palette.iconBg,
            border: `1px solid ${palette.iconBorder}`,
            color: palette.iconColor,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {palette.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>
          <div
            style={{
              fontSize: 'var(--text-xs)',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {titleForMode(t, mode, pendingActionLabel)}
          </div>
          <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-tertiary)' }}>
            {subtitleForMode(t, mode)}
          </div>
        </div>
        {onNavSettings && mode !== 'pending' ? (
          <Button variant="ghost" size="sm" onClick={onNavSettings} data-ls-action="adjust-voice">
            {t('thread.footer.adjustVoice')} →
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function paletteForMode(mode: FooterMode): {
  bg: string;
  border: string;
  iconBg: string;
  iconBorder: string;
  iconColor: string;
  icon: string;
} {
  switch (mode) {
    case 'pending':
      return {
        bg: 'var(--magenta-bg)',
        border: 'var(--magenta-border)',
        iconBg: 'var(--magenta-bg)',
        iconBorder: 'var(--magenta-border)',
        iconColor: 'var(--magenta)',
        icon: '⏸',
      };
    case 'auto':
      return {
        bg: 'var(--success-bg)',
        border: 'var(--success-border)',
        iconBg: 'var(--success-bg)',
        iconBorder: 'var(--success-border)',
        iconColor: 'var(--success)',
        icon: '🤖',
      };
    case 'review':
      return {
        bg: 'var(--accent-dim)',
        border: 'var(--accent-border)',
        iconBg: 'var(--accent-dim)',
        iconBorder: 'var(--accent-border)',
        iconColor: 'var(--accent)',
        icon: '✦',
      };
    case 'hot-override':
      return {
        bg: 'var(--error-bg)',
        border: 'var(--error-border)',
        iconBg: 'var(--error-bg)',
        iconBorder: 'var(--error-border)',
        iconColor: 'var(--error)',
        icon: '🔥',
      };
  }
}

function titleForMode(
  t: (key: string, params?: Record<string, unknown>) => string,
  mode: FooterMode,
  pendingActionLabel: string | undefined,
): string {
  switch (mode) {
    case 'pending':
      return t('thread.footer.pendingTitle', { label: pendingActionLabel ?? '' });
    case 'auto':
      return t('thread.footer.autoTitle');
    case 'review':
      return t('thread.footer.reviewTitle');
    case 'hot-override':
      return t('thread.footer.hotOverrideTitle');
  }
}

function subtitleForMode(t: (key: string) => string, mode: FooterMode): string {
  switch (mode) {
    case 'pending':
      return t('thread.footer.pendingSubtitle');
    case 'auto':
      return t('thread.footer.autoSubtitle');
    case 'review':
      return t('thread.footer.reviewSubtitle');
    case 'hot-override':
      return t('thread.footer.hotOverrideSubtitle');
  }
}
